import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, X, Activity, AlertCircle, CheckCircle, Clock, DollarSign, ClipboardList, Target } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Chatbot from './Chatbot';
import LoginPage from './LoginPage';

const BudgetDashboard = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [budgetData, setBudgetData] = useState([]);
  const [agentActivities, setAgentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'domain', direction: 'asc' });
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRow, setSelectedRow] = useState(null);
  const [agentPanelExpanded, setAgentPanelExpanded] = useState(false);
  const [agentQueue, setAgentQueue] = useState([]);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [editableData, setEditableData] = useState(null);

  // Load CSV data on component mount - only after authentication
  useEffect(() => {
    if (!isAuthenticated) return;

    const loadData = async () => {
      try {
        setLoading(true);
        
        const budgetResponse = await fetch('/data/budget_data.csv');
        const budgetText = await budgetResponse.text();
        const budgetParsed = parseCSV(budgetText);
        setBudgetData(budgetParsed);

        const agentResponse = await fetch('/data/agent_activities.csv');
        const agentText = await agentResponse.text();
        const agentParsed = parseCSV(agentText);
        
        setAgentActivities(agentParsed.slice(0, 2));
        setAgentQueue(agentParsed.slice(2));

        setLoading(false);
        setTimeout(() => setIsChatOpen(true), 1000);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, [isAuthenticated]);

  useEffect(() => {
    if (agentQueue.length > 0) {
      const interval = setInterval(() => {
        setAgentQueue(prevQueue => {
          if (prevQueue.length === 0) {
            clearInterval(interval);
            return [];
          }
          const nextActivity = prevQueue[0];
          const remainingQueue = prevQueue.slice(1);
          
          setAgentActivities(prevActivities => [...prevActivities, nextActivity]);
          return remainingQueue;
        });
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [agentQueue]);
  
  useEffect(() => {
    if (selectedRow) {
      setEditableData({ ...selectedRow });
    } else {
      setEditableData(null);
    }
  }, [selectedRow]);

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      const obj = {};
      
      headers.forEach((header, index) => {
        let value = values[index];
        
        // Convert numeric strings to numbers - more robust handling
        if (!isNaN(value) && value !== '' && value !== null && value !== undefined) {
          value = parseFloat(value);
        }
        
        obj[header] = value;
      });
      
      return obj;
    });
  };
  
  const handleModalDataChange = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    
    setEditableData(prevData => {
      const newData = {
        ...prevData,
        [field]: numericValue,
      };
      
      const totalActuals = (newData.mjCharges || 0) + (newData.cjtCharges || 0) + (newData.timesheetActuals || 0) + (newData.poActuals || 0);
      
      const planningVariance = (newData.allocatedBudget || 0) - (newData.plannedBudget || 0);
      const allocatedVariance = totalActuals - (newData.allocatedBudget || 0);
      
      const planningVariancePercent = (newData.plannedBudget || 0) !== 0 ? (planningVariance / newData.plannedBudget) * 100 : 0;
      const allocatedVariancePercent = (newData.allocatedBudget || 0) !== 0 ? (allocatedVariance / newData.allocatedBudget) * 100 : 0;
      
      return {
        ...newData,
        totalActuals,
        planningVariance,
        allocatedVariance,
        planningVariancePercent,
        allocatedVariancePercent,
      };
    });
  };

  const processedData = useMemo(() => {
    return budgetData.map((row) => {
      const mjCharges = parseFloat(row.mjCharges) || 0;
      const cjtCharges = parseFloat(row.cjtCharges) || 0;
      const timesheetActuals = parseFloat(row.timesheetActuals) || 0;
      const poActuals = parseFloat(row.poActuals) || 0;
      const plannedBudget = parseFloat(row.plannedBudget) || 0;
      const allocatedBudget = parseFloat(row.allocatedBudget) || 0;
      
      const totalActuals = mjCharges + cjtCharges + timesheetActuals + poActuals;
      
      const planningVariance = allocatedBudget - plannedBudget;
      const planningVariancePercent = plannedBudget !== 0 ? (planningVariance / plannedBudget) * 100 : 0;

      const allocatedVariance = totalActuals - allocatedBudget;
      const allocatedVariancePercent = allocatedBudget !== 0 ? (allocatedVariance / allocatedBudget) * 100 : 0;

      return { 
        ...row,
        plannedBudget,
        allocatedBudget,
        mjCharges,
        cjtCharges,
        timesheetActuals,
        poActuals,
        totalActuals, 
        planningVariance, 
        planningVariancePercent,
        allocatedVariance,
        allocatedVariancePercent
      };
    });
  }, [budgetData]);

  const filteredAndSortedData = useMemo(() => {
    let filtered = processedData.filter(row => {
      const matchesSearch = row.domain.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           row.wbsCode.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' || row.status === filterStatus;
      return matchesSearch && matchesFilter;
    });

    filtered.sort((a, b) => {
      if (sortConfig.key) {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return filtered;
  }, [processedData, searchTerm, filterStatus, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const formatCurrency = (value) => {
    const numValue = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD', 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(numValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800 border-green-300';
      case 'warning': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'error': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-red-700';
    if (variance < 0) return 'text-green-700';
    return 'text-gray-700';
  };

  const totalPlannedBudget = processedData.reduce((sum, row) => sum + (parseFloat(row.plannedBudget) || 0), 0);
  const totalAllocatedBudget = processedData.reduce((sum, row) => sum + (parseFloat(row.allocatedBudget) || 0), 0);
  const totalActuals = processedData.reduce((sum, row) => sum + (parseFloat(row.totalActuals) || 0), 0);
  
  const totalPlanningVariance = totalAllocatedBudget - totalPlannedBudget;
  const totalAllocatedVariance = totalActuals - totalAllocatedBudget;

  const itemsNeedingReview = processedData.filter(row => row.status === 'error' || row.status === 'warning').length;
  const activeAgents = agentActivities.filter(a => a.status === 'in-progress').length;

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage onLogin={() => setIsAuthenticated(true)} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Activity size={48} className="text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-lg text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="text-red-600 mx-auto mb-4" />
          <p className="text-lg text-red-600">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Financial Planning & Analysis Dashboard</h1>
              <p className="text-slate-300 text-sm mt-1">Intelligent tracking with AI-powered validation</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-xs text-slate-400">Last Updated</div>
                <div className="text-sm font-medium">Nov 18, 2025 09:22 AM</div>
              </div>
        
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                <Activity size={16} />
                <span className="text-sm">{activeAgents} Agents Active</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <ClipboardList size={16} className="mr-2" />
              Total Planned
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalPlannedBudget)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <Target size={16} className="mr-2" />
              Total Allocated
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalAllocatedBudget)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
            <div className="flex items-center text-sm text-gray-600 mb-1">
              <DollarSign size={16} className="mr-2" />
              Total Actuals
            </div>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(totalActuals)}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Planning Variance</div>
            <div className={`text-2xl font-bold ${getVarianceColor(totalPlanningVariance)}`}>
              {formatCurrency(Math.abs(totalPlanningVariance))}
              <span className="text-sm ml-2">({totalPlanningVariance >= 0 ?
 '+' : ''}{totalPlannedBudget !== 0 ? ((totalPlanningVariance / totalPlannedBudget) * 100).toFixed(1) : '0.0'}%)</span>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-5 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Allocated Variance</div>
            <div className={`text-2xl font-bold ${getVarianceColor(totalAllocatedVariance)}`}>
              {formatCurrency(Math.abs(totalAllocatedVariance))}
              <span className="text-sm ml-2">({totalAllocatedVariance >= 0 ?
 '+' : ''}{totalAllocatedBudget !== 0 ? ((totalAllocatedVariance / totalAllocatedBudget) * 100).toFixed(1) : '0.0'}%)</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow mb-4 p-4 border border-gray-200">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by domain or WBS code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
      
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="success">On Track</option>
              <option value="warning">Minor Variance</option>
              <option value="error">Needs Review</option>
            </select>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-20">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="text-left px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('domain')}>
                    <div className="flex items-center gap-2">
                      Domain
                      {sortConfig.key === 'domain' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-left px-6 py-4 font-semibold">WBS Code</th>
                  <th className="text-right px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('plannedBudget')}>
                    <div className="flex items-center justify-end gap-2">
                      Planned Budget
                      {sortConfig.key === 'plannedBudget' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('allocatedBudget')}>
                    <div className="flex items-center justify-end gap-2">
                      Allocated Budget
                      {sortConfig.key === 'allocatedBudget' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('totalActuals')}>
                    <div className="flex items-center justify-end gap-2">
                      Actuals
                      {sortConfig.key === 'totalActuals' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('planningVariance')}>
                    <div className="flex items-center justify-end gap-2">
                      Plan Variance
                      {sortConfig.key === 'planningVariance' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-right px-6 py-4 font-semibold cursor-pointer hover:bg-slate-700" onClick={() => handleSort('allocatedVariance')}>
                    <div className="flex items-center justify-end gap-2">
                      Alloc. Variance
                      {sortConfig.key === 'allocatedVariance' && (sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
                    </div>
                  </th>
                  <th className="text-center px-6 py-4 font-semibold">Status</th>
                  <th className="text-center px-6 py-4 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAndSortedData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-slate-900">{row.domain}</td>
                    <td className="px-6 py-4 text-gray-600 font-mono text-sm">{row.wbsCode}</td>
                    <td className="px-6 py-4 text-right font-medium">{formatCurrency(row.plannedBudget)}</td>
                    <td className="px-6 py-4 text-right font-medium">{formatCurrency(row.allocatedBudget)}</td>
                    <td className="px-6 py-4 text-right font-medium">{formatCurrency(row.totalActuals)}</td>
                    <td className={`px-6 py-4 text-right font-bold ${getVarianceColor(row.planningVariance)}`}>
                      {formatCurrency(Math.abs(row.planningVariance))}
                      <div className="text-xs mt-1">
                        ({row.planningVariance >= 0 ? '+' : ''}{row.planningVariancePercent.toFixed(1)}%)
                      </div>
                    </td>
                    <td className={`px-6 py-4 text-right font-bold ${getVarianceColor(row.allocatedVariance)}`}>
                      {formatCurrency(Math.abs(row.allocatedVariance))}
                      <div className="text-xs mt-1">
                        ({row.allocatedVariance >= 0 ? '+' : ''}{row.allocatedVariancePercent.toFixed(1)}%)
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">


                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusColor(row.status)}`}>
                        {row.status === 'success' ? 'On Track' : row.status === 'warning' ? 'Minor Variance' : 'Needs Review'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button 
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        onClick={() => setSelectedRow(row)}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-6">
          <div 
            className="bg-slate-800 text-white p-4 flex justify-between items-center cursor-pointer hover:bg-slate-700 transition-colors"
            onClick={() => setAgentPanelExpanded(!agentPanelExpanded)}
          >
            <div className="flex items-center gap-2">
              <Activity size={20} />
              <h2 className="text-lg font-semibold">Agent Activities ({agentActivities.length})</h2>
            </div>
            {agentPanelExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
          
          {agentPanelExpanded && (
            <div className="p-6">
              <div className="space-y-3">
                {agentActivities.map((activity, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        {activity.status === 'completed' && <CheckCircle size={20} className="text-green-600" />}
                        {activity.status === 'in-progress' && <Clock size={20} className="text-blue-600 animate-spin" />}
                        {activity.status === 'failed' && <AlertCircle size={20} className="text-red-600" />}
                        <span className="font-semibold text-slate-900">{activity.agent}</span>
                      </div>
                      <span className="text-xs text-gray-500">{activity.timestamp}</span>
                    </div>
                    <p className="text-sm text-gray-700">{activity.action}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedRow && editableData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{selectedRow.domain}</h2>
                <p className="text-slate-300 text-sm mt-1">{selectedRow.wbsCode}</p>
              </div>
              <button 
                onClick={() => setSelectedRow(null)}
                className="text-white hover:bg-slate-700 p-2 rounded-lg transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto flex">
              <div className="w-3/5 p-6 border-r border-gray-200">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Budget Analysis</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Planning Variance (Allocated vs. Planned)</div>
                    <div className={`text-3xl font-bold ${getVarianceColor(editableData.planningVariance)}`}>
                      {formatCurrency(Math.abs(editableData.planningVariance))}
                    </div>
                    <div className="text-lg text-gray-600 mt-1">
                      ({editableData.planningVariance >= 0 ? '+' : ''}{editableData.planningVariancePercent.toFixed(1)}%)
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Allocated Variance (Actuals vs. Allocated)</div>
                    <div className={`text-3xl font-bold ${getVarianceColor(editableData.allocatedVariance)}`}>
                      {formatCurrency(Math.abs(editableData.allocatedVariance))}
                    </div>
                    <div className="text-lg text-gray-600 mt-1">
                      ({editableData.allocatedVariance >= 0 ? '+' : ''}{editableData.allocatedVariancePercent.toFixed(1)}%)
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">How We Calculated This (Try editing values)</h4>
                  <div className="space-y-2 bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Planned Budget:</span>
                      <input
                        type="number"
                        value={editableData.plannedBudget}
                        onChange={(e) => handleModalDataChange('plannedBudget', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Allocated Budget:</span>
                      <input
                        type="number"
                        value={editableData.allocatedBudget}
                        onChange={(e) => handleModalDataChange('allocatedBudget', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-gray-700">MJ x-charges:</span>
                      <input
                        type="number"
                        value={editableData.mjCharges}
                        onChange={(e) => handleModalDataChange('mjCharges', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">CJT digital charges:</span>
                      <input
                        type="number"
                        value={editableData.cjtCharges}
                        onChange={(e) => handleModalDataChange('cjtCharges', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">Timesheet actuals:</span>
                      <input
                        type="number"
                        value={editableData.timesheetActuals}
                        onChange={(e) => handleModalDataChange('timesheetActuals', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">PO actuals:</span>
                      <input
                        type="number"
                        value={editableData.poActuals}
                        onChange={(e) => handleModalDataChange('poActuals', e.target.value)}
                        className="font-medium text-right w-36 p-1 border rounded"
                      />
                    </div>
                    
                    <div className="flex justify-between pt-2 border-t-2 border-gray-300">
                      <span className="text-gray-900 font-semibold">Total Actuals:</span>
                      <span className="font-bold">{formatCurrency(editableData.totalActuals)}</span>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">Visual Analysis</h4>
                  <div className="h-64 w-full bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={[{
                          name: editableData.domain,
                          Planned: editableData.plannedBudget,
                          Allocated: editableData.allocatedBudget,
                          Actuals: editableData.totalActuals
                        }]}
                        margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                      >
                        <XAxis dataKey="name" />
                        <YAxis tickFormatter={formatCurrency} />
                        <Tooltip formatter={formatCurrency} />
                        <Legend />
                        <Bar dataKey="Planned" fill="#A8B3C5" />
                        <Bar dataKey="Allocated" fill="#8884d8" />
                        <Bar dataKey="Actuals" fill={editableData.allocatedVariance > 0 ? '#DC2626' : '#16A34A'} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="w-2/5 p-6 bg-gray-50 overflow-y-auto">
                <h3 className="text-lg font-bold text-slate-900 mb-4">Agent Actions & AI Analysis</h3>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-slate-900 mb-3">AI Analysis</h4>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-2">
                      The allocated variance is primarily driven by higher-than-expected MJ cross-charges and PO actuals.
                      This suggests potential scope creep or resource allocation issues that require attention.
                    </p>
                    <p className="text-sm text-gray-700">
                      Recommendation: Review recent purchase orders and validate timesheet entries against project milestones.
                    </p>
                  </div>
                </div>
                
                <h4 className="font-semibold text-slate-900 mb-3">Agent Log for this Item</h4>
                <div className="space-y-3">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={20} className="text-green-600" />
                      <span className="font-semibold text-sm">Validation Complete</span>
                    </div>
                    <p className="text-xs text-gray-600">All WBS codes validated against SAP master data</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={20} className="text-green-600" />
                      <span className="font-semibold text-sm">Reconciliation Complete</span>
                    </div>
                    <p className="text-xs text-gray-600">MJ and CJT entries reconciled successfully</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle size={20} className="text-amber-600" />
                      <span className="font-semibold text-sm">Review Required</span>
                    </div>
                    <p className="text-xs text-gray-600">Variance exceeds threshold - manual review recommended</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Chatbot
        budgetData={processedData}
        itemsNeedingReview={itemsNeedingReview}
        totalPlannedBudget={totalPlannedBudget}
        totalAllocatedBudget={totalAllocatedBudget}
        totalActuals={totalActuals}
        totalPlanningVariance={totalPlanningVariance}
        totalAllocatedVariance={totalAllocatedVariance}
        formatCurrency={formatCurrency}
        setFilterStatus={setFilterStatus}
        setSearchTerm={setSearchTerm}
        setSelectedRow={setSelectedRow}
        isChatOpen={isChatOpen}
        setIsChatOpen={setIsChatOpen}
      />
    </div>
  );
};

export default BudgetDashboard;